import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import moment, { Moment } from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/zh-cn';
// `moment/locale/es` FALTABA. Importar un locale lo deja como global de moment,
// así que sin este import el global quedaba en `zh-cn` (el último importado) y
// `moment.locale('es')` de index.tsx no hacía nada: los meses salían en chino
// ("9月"). Va al final para que `es` sea el global al cargar el módulo.
import 'moment/locale/es';
import Icon from '../../components/forms/icon.component';
import { useDarkMode } from '../../hooks/use-dark-mode.hook';
import './date-picker.component.scss';

declare const $: any;

type PresetKey =
  | 'current_month'
  | 'previous_month'
  | 'last_7_days'
  | 'last_24_hours'
  | 'today'
  | 'yesterday'
  | 'custom';

type Range = { start: Moment; end: Moment };

type Props = {
  onDatesChange?: (r: Range) => void;
  optional?: boolean;
  label?: string;
  requiredIndicator?: string;
  errors?: boolean;
  include24Hours?: boolean;
  default24Hours?: boolean;
  las7Days?: boolean;
  defaultPreset?: PresetKey;
  initialStart?: string | Date | Moment;
  initialEnd?: string | Date | Moment;
  maxSelectableDate?: string | Date | Moment;
  [key: string]: any;
};

const mapLanguageToMoment = (lang?: string) => {
  const normalizedLanguage = (lang || 'en').toLowerCase().replace('_', '-');
  const baseLanguage = normalizedLanguage.split('-')[0];

  switch (baseLanguage) {
    case 'zh':
      return 'zh-cn';
    case 'es':
      return 'es';
    case 'en':
    default:
      return 'en-gb';
  }
};

const applyLocaleToRange = (range: Range, locale: string): Range => ({
  start: range.start.clone().locale(locale),
  end: range.end.clone().locale(locale)
});

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const getMaxAllowedDate = (maxSelectableDate?: Props['maxSelectableDate']) => {
  const parsed = maxSelectableDate ? moment(maxSelectableDate) : moment();
  return parsed.isValid() ? parsed.endOf('day') : moment().endOf('day');
};

const getDisplayLabel = (
  start: Moment,
  end: Moment,
  t: TFunction,
  locale: string
): React.ReactNode => {
  const now = moment().locale(locale);
  const fmt = (m: Moment) => capitalize(m.clone().locale(locale).format('D MMM. YYYY'));
  const sameDay = start.isSame(end, 'day');

  const todayS = now.clone().startOf('day');
  const todayE = now.clone().endOf('day');
  if (start.isSame(todayS, 'day') && end.isSame(todayE, 'day') && sameDay) {
    return <span>{t('components.datepicker.today')}</span>;
  }

  const yestS = now.clone().subtract(1, 'days').startOf('day');
  const yestE = now.clone().subtract(1, 'days').endOf('day');
  if (start.isSame(yestS, 'day') && end.isSame(yestE, 'day') && sameDay) {
    return <span>{t('components.datepicker.yesterday')}</span>;
  }

  const l7S = now.clone().subtract(6, 'days').startOf('day');
  if (start.isSame(l7S, 'day') && end.isSame(todayE, 'day')) {
    return <span>{t('components.datepicker.last_7_days')}</span>;
  }

  const l30S = now.clone().subtract(29, 'days').startOf('day');
  if (start.isSame(l30S, 'day') && end.isSame(todayE, 'day')) {
    return <span>{t('components.datepicker.last_30_days')}</span>;
  }

  const curMonthS = now.clone().startOf('month');
  const curMonthE = now.clone().endOf('month');
  if (start.isSame(curMonthS, 'day') && end.isSame(curMonthE, 'day'))
    return <span>{capitalize(now.clone().format('MMMM YYYY'))}</span>;

  const prevMonthS = now.clone().subtract(1, 'month').startOf('month');
  const prevMonthE = now.clone().subtract(1, 'month').endOf('month');
  if (start.isSame(prevMonthS, 'day') && end.isSame(prevMonthE, 'day'))
    return <span>{capitalize(prevMonthS.format('MMMM YYYY'))}</span>;

  if (sameDay) return <span>{fmt(start)}</span>;

  if (start.isSame(end, 'month'))
    return (
      <span>
        {start.clone().locale(locale).format('D')} —{' '}
        {capitalize(end.clone().locale(locale).format('D MMM. YYYY'))}
      </span>
    );

  return (
    <span>
      {capitalize(start.clone().locale(locale).format('D MMM.'))} — {fmt(end)}
    </span>
  );
};

const getRangeFromPreset = (
  key: PresetKey | undefined,
  initialStart?: Props['initialStart'],
  initialEnd?: Props['initialEnd'],
  maxSelectableDate?: Props['maxSelectableDate']
): Range => {
  const maxDate = getMaxAllowedDate(maxSelectableDate);

  switch (key) {
    case 'last_24_hours':
      return {
        start: maxDate.clone().subtract(24, 'hours'),
        end: maxDate.clone()
      };

    case 'last_7_days':
      return {
        start: maxDate.clone().subtract(6, 'days').startOf('day'),
        end: maxDate.clone()
      };

    case 'today':
      return {
        start: maxDate.clone().startOf('day'),
        end: maxDate.clone()
      };

    case 'yesterday':
      return {
        start: maxDate.clone().subtract(1, 'days').startOf('day'),
        end: maxDate.clone().subtract(1, 'days').endOf('day')
      };

    case 'previous_month':
      return {
        start: maxDate.clone().subtract(1, 'month').startOf('month'),
        end: maxDate.clone().subtract(1, 'month').endOf('month')
      };

    case 'current_month':
      return {
        start: maxDate.clone().startOf('month'),
        end: maxDate.clone()
      };

    case 'custom':
      return { start: maxDate.clone().startOf('day'), end: maxDate.clone() };

    default:
      return {
        start: maxDate.clone().startOf('day'),
        end: maxDate.clone()
      };
  }
};

/**
 * Rango inicial del control.
 *
 * Si el padre pasa `initialStart`/`initialEnd`, **esos mandan** sobre el
 * preset: son el estado real con el que la pantalla ya está consultando, y
 * antes se ignoraban salvo con `defaultPreset='custom'`. Por eso /reports
 * consultaba "hoy" pero el botón decía "1 — 9 sep": la etiqueta mentía.
 */
const getInitialRange = (
  key: PresetKey | undefined,
  initialStart?: Props['initialStart'],
  initialEnd?: Props['initialEnd'],
  maxSelectableDate?: Props['maxSelectableDate']
): Range => {
  if (initialStart !== undefined && initialEnd !== undefined) {
    const maxDate = getMaxAllowedDate(maxSelectableDate);
    const parsedStart = moment(initialStart);
    const parsedEnd = moment(initialEnd);
    if (parsedStart.isValid() && parsedEnd.isValid()) {
      // Una fecha suelta ('YYYY-MM-DD') se parsea a las 00:00; el fin del rango
      // tiene que cubrir el día completo.
      const normalizedEnd = parsedEnd.isSame(parsedEnd.clone().startOf('day'))
        ? parsedEnd.clone().endOf('day')
        : parsedEnd;
      const safeEnd = normalizedEnd.isAfter(maxDate) ? maxDate.clone() : normalizedEnd;
      const safeStart = parsedStart.isAfter(safeEnd) ? safeEnd.clone().startOf('day') : parsedStart;
      return { start: safeStart, end: safeEnd };
    }
  }
  return getRangeFromPreset(key, initialStart, initialEnd, maxSelectableDate);
};

/** Milisegundos del valor efectivo, o '' si no aplica. Sirve como dependencia
 *  estable de los efectos: un `moment()` recién creado cambia en cada render. */
const msKey = (v?: string | Date | Moment): string => {
  if (v === undefined || v === null) return '';
  const m = moment(v);
  return m.isValid() ? String(m.valueOf()) : '';
};

export default React.forwardRef<HTMLDivElement, Props>(
  (
    {
      onDatesChange,
      optional = false,
      label,
      requiredIndicator = '',
      errors,
      include24Hours = false,
      defaultPreset = 'today',
      initialStart,
      initialEnd,
      maxSelectableDate,
      ...register
    },
    ref
  ) => {
    const { t, i18n } = useTranslation();
    const isDark = useDarkMode();
    const currentLocale = mapLanguageToMoment(i18n.resolvedLanguage || i18n.language);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [rangeFilter, setRangeFilter] = useState<Range>(() =>
      applyLocaleToRange(
        getInitialRange(defaultPreset, initialStart, initialEnd, maxSelectableDate),
        currentLocale
      )
    );

    /**
     * Dependencias ESTABLES para los efectos de abajo.
     *
     * `maxSelectableDate` se pasaba como `moment()` en las tres pantallas: un
     * objeto nuevo en cada render. Como estaba en el array de dependencias del
     * efecto que monta el daterangepicker, el widget se destruía y se volvía a
     * crear en CADA render — y como abrirlo hace `setIsOpen(true)`, se cerraba
     * solo en el instante en que lo abrías. `getMaxAllowedDate` normaliza a fin
     * de día, así que la llave sí es estable dentro del mismo día.
     */
    const maxDateMs = getMaxAllowedDate(maxSelectableDate).valueOf();
    const initialStartKey = msKey(initialStart);
    const initialEndKey = msKey(initialEnd);
    const [isOpen, setIsOpen] = useState(false);

    const syncDropdownTheme = useCallback(() => {
      const drp = buttonRef.current ? $(buttonRef.current).data('daterangepicker') : null;
      if (!drp?.container) return;

      drp.container.addClass('cb-date-picker-dropdown');
      drp.container.toggleClass('cb-date-picker-dropdown-dark', isDark);
    }, [isDark]);

    const getRanges = () => {
      const maxDate = moment(maxDateMs);

      if (include24Hours) {
        return {
          [t('components.datepicker.last_24_hours')]: [
            maxDate.clone().subtract(24, 'hours'),
            maxDate.clone()
          ],
          [t('components.datepicker.last_7_days')]: [
            maxDate.clone().subtract(6, 'days').startOf('day'),
            maxDate.clone()
          ],
          [t('components.datepicker.last_30_days')]: [
            maxDate.clone().subtract(29, 'days').startOf('day'),
            maxDate.clone()
          ],
          [t('components.datepicker.current_month')]: [
            maxDate.clone().startOf('month'),
            maxDate.clone()
          ],
          [t('components.datepicker.previous_month')]: [
            maxDate.clone().subtract(1, 'month').startOf('month'),
            maxDate.clone().subtract(1, 'month').endOf('month')
          ]
        };
      }

      return {
        [t('components.datepicker.today')]: [maxDate.clone().startOf('day'), maxDate.clone()],
        [t('components.datepicker.yesterday')]: [
          maxDate.clone().subtract(1, 'days').startOf('day'),
          maxDate.clone().subtract(1, 'days').endOf('day')
        ],
        [t('components.datepicker.last_7_days')]: [
          maxDate.clone().subtract(6, 'days').startOf('day'),
          maxDate.clone()
        ],
        [t('components.datepicker.last_30_days')]: [
          maxDate.clone().subtract(29, 'days').startOf('day'),
          maxDate.clone()
        ],
        [t('components.datepicker.current_month')]: [
          maxDate.clone().startOf('month'),
          maxDate.clone()
        ],
        [t('components.datepicker.previous_month')]: [
          maxDate.clone().subtract(1, 'month').startOf('month'),
          maxDate.clone().subtract(1, 'month').endOf('month')
        ]
      };
    };

    useEffect(() => {
      setRangeFilter(getInitialRange(defaultPreset, initialStart, initialEnd, maxSelectableDate));
      // Dependencias por valor (ver `maxDateMs` arriba), no por referencia.
      // eslint-disable-next-line
    }, [defaultPreset, initialStartKey, initialEndKey, maxDateMs]);

    useEffect(() => {
      const locale = mapLanguageToMoment(i18n.resolvedLanguage || i18n.language);
      moment.locale(locale);
      setRangeFilter((previousRange) => applyLocaleToRange(previousRange, locale));
    }, [i18n.language, i18n.resolvedLanguage]);

    useEffect(() => {
      if (!buttonRef.current) return;
      const maxDate = moment(maxDateMs);
      const existing = $(buttonRef.current).data('daterangepicker');
      if (existing) {
        existing.remove();
      }

      const cb = (start: Moment, end: Moment) => {
        const locale = mapLanguageToMoment(i18n.resolvedLanguage || i18n.language);
        moment.locale(locale);
        const localizedRange = applyLocaleToRange({ start, end }, locale);
        setRangeFilter(localizedRange);
        onDatesChange?.(localizedRange);
      };

      $(buttonRef.current)
        .off('show.daterangepicker hide.daterangepicker')
        .on('show.daterangepicker', () => {
          syncDropdownTheme();
          setIsOpen(true);
        })
        .on('hide.daterangepicker', () => setIsOpen(false));

      $(buttonRef.current).daterangepicker(
        {
          autoUpdateInput: false,
          startDate: rangeFilter.start,
          endDate: rangeFilter.end,
          maxDate,
          drops: 'down',
          opens: 'left',
          ranges: getRanges(),
          locale: {
            applyLabel: t('components.datepicker.apply'),
            cancelLabel: t('components.datepicker.cancel'),
            customRangeLabel: t('components.datepicker.customize'),
            daysOfWeek: [
              t('components.datepicker.sunday'),
              t('components.datepicker.monday'),
              t('components.datepicker.tuesday'),
              t('components.datepicker.wednesday'),
              t('components.datepicker.thursday'),
              t('components.datepicker.friday'),
              t('components.datepicker.saturday')
            ],
            monthNames: [
              t('components.datepicker.january'),
              t('components.datepicker.february'),
              t('components.datepicker.march'),
              t('components.datepicker.april'),
              t('components.datepicker.may'),
              t('components.datepicker.june'),
              t('components.datepicker.july'),
              t('components.datepicker.august'),
              t('components.datepicker.september'),
              t('components.datepicker.october'),
              t('components.datepicker.november'),
              t('components.datepicker.december')
            ]
          }
        },
        cb
      );
      syncDropdownTheme();

      return () => {
        const drp = $(buttonRef.current).data('daterangepicker');
        if (drp) drp.remove();
      };
      // eslint-disable-next-line
    }, [i18n.language, include24Hours, maxDateMs, syncDropdownTheme]);

    return (
      <div ref={ref}>
        {label && (
          <label className={'form-label fw-bolder ' + (requiredIndicator || '')}>
            {label}{' '}
            {optional && <span className="fs-8 ms-1 text-dark">({t('general.optional')})</span>}
          </label>
        )}

        <div className={'d-flex ' + (errors ? 'is-invalid' : '')}>
          <div className="card shadow-sm border-0">
            <button
              type="button"
              title="date"
              ref={buttonRef}
              className="btn rounded-pill d-inline-flex align-items-center gap-2 px-4 py-3 hover-scale"
              style={{ background: 'transparent', border: '0', maxWidth: 300 }}
              {...register}
            >
              <Icon icon="ki-calendar-8" size="fs-2" type="primary" className="flex-shrink-0" />
              <span
                className="fw-bolder text-body fs-6 text-nowrap"
                style={{ overflow: 'hidden', display: 'inline-flex' }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={
                      rangeFilter.start && rangeFilter.end
                        ? `${rangeFilter.start.valueOf()}-${rangeFilter.end.valueOf()}`
                        : 'empty'
                    }
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                  >
                    {rangeFilter.start && rangeFilter.end
                      ? getDisplayLabel(rangeFilter.start, rangeFilter.end, t, currentLocale)
                      : t('components.datepicker.select_date')}
                  </motion.span>
                </AnimatePresence>
              </span>
              <motion.span
                className="flex-shrink-0 ms-1"
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                style={{ display: 'inline-flex' }}
              >
                <Icon icon="ki-down" size="fs-7" type="muted" />
              </motion.span>
            </button>
          </div>
        </div>
      </div>
    );
  }
);
